/**
 * Serviço para gerenciar modelos de IA
 */

import { getToken } from "./tokenService";
import type {
  AIModel,
  CreateAIModelRequest,
  UpdateAIModelRequest,
  AIModelsListResponse,
  AIModelsStats,
  AIProvider,
  AIModelType,
} from "@/types/aiModels";

import { API_BASE_URL } from "@/lib/apiConfig";

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: string;
  };
  message?: string;
}

/**
 * Faz uma requisição autenticada para a API
 */
async function apiRequest<T>(
  endpoint: string,
  method: string = "GET",
  body?: any
): Promise<ApiResponse<T>> {
  const token = getToken();

  if (!token) {
    throw new Error("Token de autenticação não encontrado");
  }

  const config: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  if (body && method !== "GET") {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || {
          code: "HTTP_ERROR",
          message: `Erro HTTP: ${response.status}`,
        },
      };
    }

    return data;
  } catch (error) {
    return {
      success: false,
      error: {
        code: "NETWORK_ERROR",
        message:
          error instanceof Error ? error.message : "Erro de conexão com a API",
      },
    };
  }
}

/**
 * Lista modelos de IA com filtros e paginação
 */
export async function listAIModels(options?: {
  limit?: number;
  offset?: number;
  provider_id?: number;
  type_id?: number;
  is_active?: boolean;
  is_integration_active?: boolean;
  search?: string;
}): Promise<ApiResponse<AIModelsListResponse>> {
  const params = new URLSearchParams();

  if (options?.limit) params.append("limit", options.limit.toString());
  if (options?.offset) params.append("offset", options.offset.toString());
  if (options?.provider_id)
    params.append("provider_id", options.provider_id.toString());
  if (options?.type_id) params.append("type_id", options.type_id.toString());
  if (options?.is_active !== undefined)
    params.append("is_active", options.is_active.toString());
  if (options?.is_integration_active !== undefined)
    params.append(
      "is_integration_active",
      options.is_integration_active.toString()
    );
  if (options?.search) params.append("search", options.search);

  const queryString = params.toString();
  const endpoint = `/ai-models${queryString ? `?${queryString}` : ""}`;

  return apiRequest<AIModelsListResponse>(endpoint);
}

/**
 * Obtém um modelo de IA por ID
 */
export async function getAIModelById(
  id: string
): Promise<ApiResponse<AIModel>> {
  return apiRequest<AIModel>(`/ai-models/${id}`);
}

/**
 * Cria um novo modelo de IA
 */
export async function createAIModel(
  modelData: CreateAIModelRequest
): Promise<ApiResponse<AIModel>> {
  return apiRequest<AIModel>("/ai-models", "POST", modelData);
}

/**
 * Atualiza um modelo de IA existente
 */
export async function updateAIModel(
  id: string,
  modelData: UpdateAIModelRequest
): Promise<ApiResponse<AIModel>> {
  return apiRequest<AIModel>(`/ai-models/${id}`, "PUT", modelData);
}

/**
 * Remove um modelo de IA
 */
export async function deleteAIModel(id: string): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/ai-models/${id}`, "DELETE");
}

/**
 * Alterna o status de um modelo (ativo/inativo)
 */
export async function toggleAIModelStatus(
  id: string,
  isActive: boolean
): Promise<ApiResponse<AIModel>> {
  return apiRequest<AIModel>(`/ai-models/${id}/toggle-status`, "PATCH", {
    is_active: isActive,
  });
}

export async function toggleAIModelIntegration(
  id: string,
  isIntegrationActive: boolean
): Promise<ApiResponse<AIModel>> {
  return apiRequest<AIModel>(`/ai-models/${id}/toggle-integration`, "PATCH", {
    is_integration_active: isIntegrationActive,
  });
}

/**
 * Obtém estatísticas dos modelos de IA
 */
export async function getAIModelsStats(): Promise<ApiResponse<AIModelsStats>> {
  return apiRequest<AIModelsStats>("/ai-models/stats");
}

/**
 * Lista provedores de IA disponíveis
 */
export async function listAIProviders(): Promise<ApiResponse<AIProvider[]>> {
  const response = await apiRequest<{ providers: AIProvider[] }>("/ai-providers?limit=100");
  if (response.success && response.data) {
    return {
      ...response,
      data: response.data.providers || response.data as any,
    };
  }
  return response as any;
}

/**
 * Lista tipos de modelo disponíveis
 */
export async function listAIModelTypes(): Promise<ApiResponse<AIModelType[]>> {
  return apiRequest<AIModelType[]>("/types/ai-model-types");
}
